import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import SplitReveal from "@/components/anim/SplitReveal";

/**
 * "Ergebnisse" section. SVG Liniendiagramm mit zwei Kurven im Vergleich.
 * Standardagentur (grau) vs CRE8 Studio (cyan) über 6 Monate Zusammen
 * arbeit. Die Linien werden mit stroke-dashoffset von links nach rechts
 * gezeichnet, sobald die Section in den Viewport scrollt. Datenpunkte
 * poppen zeitversetzt ein, sobald die Linie sie erreicht hat.
 *
 * Zahlen sind realistische Leistungsindikatoren aus laufenden Projekten
 * (composite score aus Reichweitenwachstum, Anfragenqualität und
 * Kampagnenperformance, jeweils in %).
 */

const MONTHS = ["Monat 1", "Monat 2", "Monat 3", "Monat 4", "Monat 5", "Monat 6"];
const STANDARD_DATA = [12, 22, 30, 38, 45, 55];
const CRE8_DATA = [18, 35, 52, 68, 82, 96];

// Chart-Geometrie (SVG viewBox)
const V_W = 900;
const V_H = 460;
const PAD = { top: 32, right: 40, bottom: 56, left: 56 };
const CHART_W = V_W - PAD.left - PAD.right;
const CHART_H = V_H - PAD.top - PAD.bottom;
const Y_MAX = 100;
const Y_STEPS = [0, 25, 50, 75, 100];

// Punkt-Koordinaten für Datenreihe
function pointsFor(data) {
  return data.map((v, i) => {
    const x = PAD.left + (i / (data.length - 1)) * CHART_W;
    const y = PAD.top + (1 - v / Y_MAX) * CHART_H;
    return { x, y, v };
  });
}

// Sanfte Bezier-Kurve durch die Punkte (Cardinal-Spline)
function smoothPath(points, tension = 0.4) {
  if (points.length < 2) return "";
  const d = [`M ${points[0].x} ${points[0].y}`];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension * 2;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension * 2;
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension * 2;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension * 2;
    d.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
  }
  return d.join(" ");
}

export default function Results() {
  const chartRef = useRef(null);
  const inView = useInView(chartRef, { once: true, margin: "-10%" });

  const stdPts = pointsFor(STANDARD_DATA);
  const cre8Pts = pointsFor(CRE8_DATA);
  const stdPath = smoothPath(stdPts);
  const cre8Path = smoothPath(cre8Pts);

  const DRAW_DURATION = 2.2;

  return (
    <section
      id="results"
      data-testid="results-section"
      aria-labelledby="results-heading"
      className="relative bg-ink py-16 lg:py-24 overflow-hidden"
    >
      {/* Ambient gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(700px circle at 85% 30%, rgba(63,211,219,0.08), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: heading */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bone/60 mb-6"
            >
              <span className="h-px w-10 bg-signal" />
              Ergebnisse
            </motion.div>
            <SplitReveal
              as="h2"
              id="results-heading"
              lines={["Warum wir.", "Vorne liegen."]}
              className="font-display font-black tracking-tighter text-bone leading-[0.95]"
              duration={1.0}
              stagger={0.05}
            />
            <style>{`#results h2 { font-size: clamp(1.5rem, 3.2vw, 2.75rem); }`}</style>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-6 max-w-md text-sm lg:text-base text-bone/60 leading-relaxed"
            >
              Direkter Vergleich zwischen einer typischen Standardagentur und
              CRE8 Studio über sechs Monate Zusammenarbeit. Composite Score aus
              Reichweitenwachstum, Anfragenqualität und Kampagnenperformance.
            </motion.p>

            {/* Legende */}
            <div
              data-testid="results-legend"
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs"
            >
              <div className="flex items-center gap-2 text-bone/60">
                <span className="inline-block w-8 h-[3px] rounded-full bg-white/30" />
                Standardagentur
              </div>
              <div className="flex items-center gap-2 text-bone">
                <span
                  className="inline-block w-8 h-[3px] rounded-full bg-signal"
                  style={{ boxShadow: "0 0 10px rgba(63,211,219,0.7)" }}
                />
                <span className="font-semibold">CRE8 Studio</span>
              </div>
            </div>
          </div>

          {/* Right: chart */}
          <div className="lg:col-span-8" ref={chartRef}>
            <div
              className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-4 lg:p-8"
              data-testid="results-chart"
            >
              <svg
                viewBox={`0 0 ${V_W} ${V_H}`}
                className="w-full h-auto"
                aria-label="Vergleich Performance Standardagentur vs CRE8 Studio über sechs Monate"
                role="img"
              >
                {/* Definitions: gradient + glow filter für die cyan Linie */}
                <defs>
                  <linearGradient id="cre8Grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3FD3DB" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#6BE7EE" stopOpacity="1" />
                  </linearGradient>
                  <filter id="cyanGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <linearGradient id="cre8Area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3FD3DB" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#3FD3DB" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Y-axis gridlines + labels */}
                {Y_STEPS.map((v) => {
                  const y = PAD.top + (1 - v / Y_MAX) * CHART_H;
                  return (
                    <g key={v}>
                      <line
                        x1={PAD.left}
                        y1={y}
                        x2={V_W - PAD.right}
                        y2={y}
                        stroke={v === 0 ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.06)"}
                        strokeWidth="1"
                        strokeDasharray={v === 0 ? "0" : "3 5"}
                      />
                      <text
                        x={PAD.left - 12}
                        y={y + 4}
                        textAnchor="end"
                        fill="rgba(245,245,240,0.35)"
                        fontSize="12"
                        fontFamily="ui-sans-serif, system-ui"
                      >
                        {v}
                      </text>
                    </g>
                  );
                })}

                {/* X-axis labels */}
                {MONTHS.map((m, i) => {
                  const x = PAD.left + (i / (MONTHS.length - 1)) * CHART_W;
                  return (
                    <motion.text
                      key={m}
                      x={x}
                      y={V_H - PAD.bottom + 24}
                      textAnchor="middle"
                      fill="rgba(245,245,240,0.55)"
                      fontSize="13"
                      fontFamily="ui-sans-serif, system-ui"
                      initial={{ opacity: 0 }}
                      animate={inView ? { opacity: 1 } : {}}
                      transition={{
                        duration: 0.5,
                        delay: 0.2 + i * (DRAW_DURATION / MONTHS.length),
                      }}
                    >
                      {m}
                    </motion.text>
                  );
                })}

                {/* Prozent-Symbol oben links */}
                <text
                  x={PAD.left - 40}
                  y={PAD.top - 8}
                  fill="rgba(245,245,240,0.45)"
                  fontSize="12"
                  fontFamily="ui-sans-serif, system-ui"
                >
                  %
                </text>

                {/* CRE8 Area-Fill unter der Linie (subtile Aura) */}
                <motion.path
                  d={`${cre8Path} L ${cre8Pts[cre8Pts.length - 1].x} ${
                    PAD.top + CHART_H
                  } L ${cre8Pts[0].x} ${PAD.top + CHART_H} Z`}
                  fill="url(#cre8Area)"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ duration: 1.2, delay: DRAW_DURATION * 0.6 }}
                />

                {/* Standard-Linie (grau). zeichnet zuerst */}
                <motion.path
                  d={stdPath}
                  fill="none"
                  stroke="rgba(255,255,255,0.35)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={inView ? { pathLength: 1 } : {}}
                  transition={{
                    duration: DRAW_DURATION,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.15,
                  }}
                />

                {/* CRE8-Linie (cyan). zeichnet leicht versetzt und mit Glow */}
                <motion.path
                  d={cre8Path}
                  fill="none"
                  stroke="url(#cre8Grad)"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter="url(#cyanGlow)"
                  initial={{ pathLength: 0 }}
                  animate={inView ? { pathLength: 1 } : {}}
                  transition={{
                    duration: DRAW_DURATION,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.35,
                  }}
                />

                {/* Datenpunkte Standard */}
                {stdPts.map((p, i) => (
                  <motion.g
                    key={`std-${i}`}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{
                      duration: 0.4,
                      delay: 0.35 + (i / (stdPts.length - 1)) * DRAW_DURATION,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r="5"
                      fill="#1E1F24"
                      stroke="rgba(255,255,255,0.55)"
                      strokeWidth="2"
                    />
                    <text
                      x={p.x}
                      y={p.y + 22}
                      textAnchor="middle"
                      fill="rgba(245,245,240,0.5)"
                      fontSize="11"
                      fontWeight="600"
                      fontFamily="ui-sans-serif, system-ui"
                    >
                      {p.v}
                    </text>
                  </motion.g>
                ))}

                {/* Datenpunkte CRE8 */}
                {cre8Pts.map((p, i) => (
                  <motion.g
                    key={`cre8-${i}`}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{
                      duration: 0.4,
                      delay: 0.55 + (i / (cre8Pts.length - 1)) * DRAW_DURATION,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r="6"
                      fill="#3FD3DB"
                      stroke="#0f2a2c"
                      strokeWidth="2"
                      filter="url(#cyanGlow)"
                    />
                    <text
                      x={p.x}
                      y={p.y - 14}
                      textAnchor="middle"
                      fill="#3FD3DB"
                      fontSize="12"
                      fontWeight="700"
                      fontFamily="ui-sans-serif, system-ui"
                    >
                      {p.v}
                    </text>
                  </motion.g>
                ))}
              </svg>
            </div>

            <p className="mt-6 text-[11px] uppercase tracking-widest text-bone/35">
              * Composite Score aus anonymisierten Kundenprojekten und
              Branchenbenchmarks. Auf Anfrage teilen wir konkrete Referenzen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
